import type { Cart, CartItem } from '@/lib/types/Cart'
import type { Initiator } from '@/services/cart'
import * as CartService from '@/services/cart'

const returnEnvelopeId = 6974588223550
const returnEnvelopeVariantId = 39770657226814
const MAX_ENVELOPES_PER_ORDER = 3

const manualEnvelopeSelectionOverride = (cart: Cart) => {
  return cart.attributes._manual_envelope_selection_override === 'yes'
}

export default function () {
  CartService.subscribe(async (cart, event, payload, initiator) => {
    // console.log({ cart, event, payload, initiator })

    await updateEnvelopes(cart, event, payload, initiator)

    const cartObjForKlaviyo = {
      total_price: cart.total_price / 100,
      $value: cart.total_price / 100,
      total_discount: cart.total_discount,
      original_total_price: cart.original_total_price / 100,
      items: cart.items,
      ...(typeof window.item !== 'undefined' ? window.item : {}),
    }

    if (window._learnq && event === 'add') {
      window._learnq.push(['track', 'Added to Cart', cartObjForKlaviyo])
    }
  })
}

async function updateEnvelopes(
  cart: Cart,
  _: string,
  payload: CartItem | null | undefined,
  initiator: 'human' | 'system',
) {
  const envelopeInCart = pouchReturnEnvelopeInCart(cart)
  if (
    envelopeInCart &&
    cart.items.filter((item) => item !== envelopeInCart).length === 0
  ) {
    // if the cart is empty except for the envelope, remove it and
    // set us back to automatic pouch mode
    await CartService.updateQuantity(envelopeInCart.key, 0, false, 'system')
    await CartService.updateAttributes(
      {
        _manual_envelope_selection_override: '',
      },
      true,
      'system',
    )
    return
  }

  if (initiator === 'human') {
    if (payload && payload.product_id === returnEnvelopeId) {
      await CartService.updateAttributes(
        {
          _manual_envelope_selection_override: 'yes',
        },
        true,
        'system',
      )
      window.mixpanel.track('cart_user_changed_envelope_quantity', {
        quantity: payload.quantity,
      })
      return
    }
  }

  if (manualEnvelopeSelectionOverride(cart)) {
    return
  }

  const cartRes = (await (await fetch('/cart?view=json-endpoint')).json()) as {
    items: {
      quantity: number
      requires_pouch_return_envelope: boolean
      n_pouches: number | null
    }[]
  }

  const numberOfPouchProducts = cartRes.items.reduce((accum, item) => {
    if (item.requires_pouch_return_envelope) {
      const nPouchesPerProduct =
        typeof item.n_pouches === 'number' && Number.isNaN(item.n_pouches)
          ? item.n_pouches
          : 1
      return accum + nPouchesPerProduct * item.quantity
    } else {
      return accum
    }
  }, 0)

  const numberOfDesiredPouches = Math.min(
    MAX_ENVELOPES_PER_ORDER,
    Math.ceil(numberOfPouchProducts / 10),
  )
  if (!envelopeInCart) {
    if (numberOfDesiredPouches > 0) {
      await addPouchReturnEnvelope(numberOfDesiredPouches)
    }
  } else if (envelopeInCart.quantity !== numberOfDesiredPouches) {
    await CartService.updateQuantity(
      envelopeInCart.key,
      numberOfDesiredPouches,
      false,
      'system',
    )
  }
}

export function pouchReturnEnvelopeInCart(cart: Cart) {
  return cart.items.find((item) => item.id === returnEnvelopeVariantId)
}

export async function addPouchReturnEnvelope(
  quantity: number,
  initiator: Initiator = 'system',
) {
  await CartService.add(
    {
      selectedVariantId: returnEnvelopeVariantId.toString(),
      quantity: Math.min(quantity, 4),
      id: returnEnvelopeId.toString(),
    },
    true,
    initiator,
  )
}
